import React, { useState, useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector, useLocalStorage } from 'app/hooks'
import { setDebugOptions, setDebug } from 'actions/appActions'
import { RootState, CustomEventType } from 'app/types'
import { IconButton, Button, Checkbox } from 'components'
import { icons } from 'app/constants'

interface DebugMessage {
    id: number
    label: string
    data: any
    isComplex: boolean
    preview: string
}

type TabType = 'messages' | 'options' | 'actions'

export function DebugPanel() {
    const dispatch = useAppDispatch()
    const debugOptions = useAppSelector((state: RootState) => state.app.debugOptions)
    
    const [collapsed, setCollapsed] = useLocalStorage('debug-panel-collapsed', false)
    const [activeTab, setActiveTab] = useState<TabType>('messages')
    const [debugMessages, setDebugMessages] = useState<DebugMessage[]>([])
    const [selectedData, setSelectedData] = useState<any>(null)
    const [showDataModal, setShowDataModal] = useState(false)
    const [copiedId, setCopiedId] = useState<number | null>(null)

    const debugCountRef = useRef(0)

    function getPreview(data: any, length: number = 20): string {
        const str = typeof data === 'string' ? data : JSON.stringify(data)
        return str.length > length ? str.substring(0, length) + '...' : str
    }

    function formatDataDisplay(data: any): string {
        return JSON.stringify(data, null, 2)
    }

    function copyToClipboard(data: any, id: number) {
        const text = typeof data === 'string' ? data : JSON.stringify(data, null, 2)
        navigator.clipboard.writeText(text).then(() => {
            setCopiedId(id)
            setTimeout(() => setCopiedId(null), 2000)
        }).catch((err) => {
            console.error('Failed to copy:', err)
        })
    }

    useEffect(() => {
        const handleDebugMessage = (event: CustomEvent) => {
            if (!event.detail) {
                return
            }

            const detail = event.detail
            const isComplex = typeof detail === 'object' && detail !== null
            const preview = getPreview(detail, 20)
            const newId = debugCountRef.current
            
            setDebugMessages((prev) => {
                // Check if the last message has the same data to avoid duplicates
                if (prev.length > 0 && JSON.stringify(prev[0].data) === JSON.stringify(detail)) {
                    return prev
                }
                
                return [
                    {
                        id: newId,
                        label: `(${newId})`,
                        data: detail,
                        isComplex,
                        preview
                    },
                    ...prev.slice(0, 9)
                ]
            })
            debugCountRef.current += 1
        }

        window.addEventListener(CustomEventType.DebugMessage as any, handleDebugMessage)
        
        return () => {
            window.removeEventListener(CustomEventType.DebugMessage as any, handleDebugMessage)
        }
    }, [])

    function handleToggleOption(key: string, value: boolean) {
        dispatch(setDebugOptions({ [key]: value }))
    }

    function handleClose() {
        dispatch(setDebug(false))
    }

    function handleShowData(data: any) {
        setSelectedData(data)
        setShowDataModal(true)
    }

    // Collapsed - show compact panel with expand button
    if (collapsed) {
        return (
            <div className="debug-panel collapsed">
                <IconButton 
                    icon={icons.bug} 
                    noBg
                    onClick={() => setCollapsed(false)}
                    title="Expand Debug Panel"
                />
            </div>
        )
    }

    // Expanded - show full panel
    return (
        <>
            <div className="debug-panel">
                <div className="debug-panel-header">
                    <div className="debug-panel-tabs">
                        <button 
                            className={`debug-tab ${activeTab === 'messages' ? 'active' : ''}`}
                            onClick={() => setActiveTab('messages')}
                        >
                            Messages
                        </button>
                        <button 
                            className={`debug-tab ${activeTab === 'options' ? 'active' : ''}`}
                            onClick={() => setActiveTab('options')}
                        >
                            Options
                        </button>
                        <button 
                            className={`debug-tab ${activeTab === 'actions' ? 'active' : ''}`}
                            onClick={() => setActiveTab('actions')}
                        >
                            Actions
                        </button>
                    </div>
                    <div className="debug-panel-actions">
                        <IconButton 
                            noBg
                            icon={icons.chevronDown} 
                            onClick={() => setCollapsed(true)}
                            title="Collapse Debug Panel"
                        />
                        <IconButton 
                            noBg
                            icon={icons.times} 
                            onClick={handleClose}
                            title="Close Debug Panel"
                        />
                    </div>
                </div>
                
                <div className="debug-panel-content">
                    {activeTab === 'messages' && (
                        <div className="debug-messages-feed">
                            {debugMessages.length === 0 && (
                                <div className="debug-empty">No debug messages</div>
                            )}
                            {debugMessages.map((msg) => (
                                <div 
                                    key={msg.id}
                                    className={`debug-message ${msg.isComplex ? 'complex' : 'string'}`}
                                >
                                    <div className="debug-message-content">
                                        <span className="debug-label">{msg.label}</span>
                                        <span className="debug-text">
                                            {msg.isComplex ? (
                                                <span className="debug-preview">{msg.preview}</span>
                                            ) : (
                                                <span className="debug-value">{msg.data}</span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="debug-message-actions">
                                        <IconButton
                                            noBg
                                            icon={copiedId === msg.id ? icons.check : icons.copy}
                                            onClick={() => copyToClipboard(msg.data, msg.id)}
                                            title="Copy to clipboard"
                                            className={copiedId === msg.id ? 'copied' : ''}
                                        />
                                        {msg.isComplex && (
                                            <IconButton
                                                noBg
                                                icon={icons.chevronRight}
                                                onClick={() => handleShowData(msg.data)}
                                                title="Expand in modal"
                                                className="debug-expand-btn"
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {activeTab === 'options' && (
                        <div className="debug-options-list">
                            <div className="debug-option">
                                <Checkbox
                                    value={debugOptions?.debugContent || false}
                                    onChange={(value) => handleToggleOption('debugContent', value)}
                                >
                                    <span>Debug Content</span>
                                </Checkbox>
                            </div>
                        </div>
                    )}

                    {activeTab === 'actions' && (
                        <div className="debug-actions-list">
                            <div className="debug-action">
                                <Button onClick={() => window.dispatchEvent(new CustomEvent(CustomEventType.TriggerNudge))}>
                                    Fire Registration Nudge
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {showDataModal && (
                <div className="debug-data-modal-overlay animate__animated animate__fadeIn animate__faster" onClick={() => setShowDataModal(false)}>
                    <div className="debug-data-modal animate__animated animate__slideInUp animate__faster" onClick={(e) => e.stopPropagation()}>
                        <div className="debug-data-modal-header">
                            <h3>Debug Data</h3>
                            <div className="debug-data-modal-header-actions">
                                <IconButton
                                    noBg
                                    icon={copiedId === -1 ? icons.check : icons.copy}
                                    onClick={() => copyToClipboard(selectedData, -1)}
                                    title="Copy to clipboard"
                                    className={copiedId === -1 ? 'copied' : ''}
                                />
                                <IconButton 
                                    noBg
                                    icon={icons.times}
                                    onClick={() => setShowDataModal(false)}
                                    title="Close"
                                />
                            </div>
                        </div>
                        <div className="debug-data-modal-content">
                            <pre>{formatDataDisplay(selectedData)}</pre>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
